import { useLoaderData, useNavigate } from '@remix-run/react';
import { useCallback, useEffect, useState } from 'react';
import { ZAPP_STORAGE_UPDATED_EVENT } from '~/hooks/actions/defaultActions/sessionStorageSet';
import { getStaticLinkPath, getStorageValue } from './getters';
import type { loader } from '~/routes/_index';
import type { AccountButtonGeneral, StaticLinks } from './types';
import { addParametersToURL } from '~/services/links';

type ProfileAvatarData = {
  imageUrl: string | null;
  userName: string | null;
  isLoaded: boolean;
};

export function useAuthButtonsOnClick(general: AccountButtonGeneral) {
  const { staticLinks, layoutIdOverride } = useLoaderData<typeof loader>();
  const navigate = useNavigate();

  const appendLayoutId = (path: string): string => {
    if (!layoutIdOverride || !path.startsWith('/')) return path;
    return addParametersToURL(path, { layoutId: layoutIdOverride });
  };

  return {
    sign_in: (e: any) => {
      e.preventDefault();
      navigate(appendLayoutId('/login'));
    },
    sign_up: () => {
      if (general.sign_up_link_type === 'external_link') {
        window.location.href = general?.sign_up_link_path || '/';
        return;
      }

      window.location.href = appendLayoutId(
        getStaticLinkPath(
          staticLinks as StaticLinks,
          general.sign_up_link_target
        )
      );
    },
    sign_out: (e: any) => {
      e.preventDefault();
      window.location.assign(appendLayoutId('/logout'));
    },
  };
}

/**
 * Reads profile avatar data from browser storage and refreshes on cross-tab updates.
 *
 * @param imageKey - Storage key for the profile image URL.
 * @param userNameKey - Storage key for the profile user name.
 * @returns Current profile image URL and user name.
 */
export function useProfileAvatarData({
  imageKey,
  userNameKey,
}: {
  imageKey: string | undefined;
  userNameKey: string | undefined;
}): ProfileAvatarData {
  const [data, setData] = useState<ProfileAvatarData>({
    imageUrl: null,
    userName: null,
    isLoaded: false,
  });

  const refresh = useCallback((): void => {
    setData({
      imageUrl: getStorageValue(imageKey),
      userName: getStorageValue(userNameKey),
      isLoaded: true,
    });
  }, [imageKey, userNameKey]);

  useEffect(() => {
    refresh();

    window.addEventListener('storage', refresh);
    window.addEventListener(ZAPP_STORAGE_UPDATED_EVENT, refresh);

    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener(ZAPP_STORAGE_UPDATED_EVENT, refresh);
    };
  }, [refresh]);

  return data;
}
